import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
  Home,
  Package,
  Handshake,
  Rocket,
  Lightbulb,
  LogIn,
  LogOut,
  UserCircle2,
  Menu,
  X,
  ShoppingBag,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import NdaGate from "./nda/NdaGate";

const NAV = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/prodotti", label: "Prodotti", icon: Package },
  { to: "/servizi", label: "Servizi", icon: Handshake },
  { to: "/percorsi", label: "Percorsi", icon: Rocket },
  { to: "/innovazione", label: "Innovazione", icon: Lightbulb },
];

export default function TimeInsideLayout() {
  const [open, setOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { totalQuantity } = useCart();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <div className="min-h-screen text-white flex flex-col">
      <div className="time-bg" />

      {/* Header */}
      <header
        data-testid="time-inside-header"
        className="sticky top-0 z-30 backdrop-blur-xl bg-black/40 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-2 group"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 live-dot shadow-[0_0_12px_rgba(16,185,129,0.7)]" />
            <span className="tracking-[0.28em] text-sm font-light text-white/90 group-hover:text-white transition-colors">
              TIME <span className="text-emerald-400">INSIDE</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  [
                    "px-4 py-2 rounded-full text-[13px] tracking-wider uppercase transition-all duration-300",
                    isActive
                      ? "bg-white/[0.06] text-white border border-white/10"
                      : "text-white/60 hover:text-white hover:bg-white/[0.03] border border-transparent",
                  ].join(" ")
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/carrello"
              data-testid="nav-cart"
              aria-label={
                totalQuantity > 0
                  ? `Carrello (${totalQuantity} ${totalQuantity === 1 ? "articolo" : "articoli"})`
                  : "Carrello vuoto"
              }
              className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/10 text-white/70 hover:text-white hover:border-white/30 hover:bg-white/[0.03] transition-colors"
            >
              <ShoppingBag className="w-4 h-4" />
              {totalQuantity > 0 && (
                <span
                  data-testid="cart-badge"
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-400 text-black text-[10px] font-mono font-medium flex items-center justify-center shadow-[0_0_10px_rgba(16,185,129,0.6)]"
                >
                  {totalQuantity}
                </span>
              )}
            </Link>
            {isAuthenticated ? (
              <div className="hidden md:flex items-center gap-2">
                <Link
                  to="/area-personale"
                  data-testid="nav-area"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500/60 transition-all text-[12px] tracking-widest uppercase"
                >
                  <UserCircle2 className="w-3.5 h-3.5" />
                  {user?.name || "Area"}
                </Link>
                <button
                  onClick={handleLogout}
                  data-testid="nav-logout"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-all text-[12px] tracking-widest uppercase"
                  aria-label="Esci"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  Esci
                </button>
              </div>
            ) : (
              <Link
                to="/auth"
                data-testid="nav-auth"
                className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 hover:border-amber-500/60 transition-all text-[12px] tracking-widest uppercase"
              >
                <LogIn className="w-3.5 h-3.5" />
                Accedi
              </Link>
            )}
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2 rounded-lg hover:bg-white/5 transition"
              aria-label="Menu"
            >
              {open ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div
            data-testid="mobile-menu"
            className="md:hidden border-t border-white/5 bg-black/70 backdrop-blur-xl"
          >
            <nav className="px-5 py-4 flex flex-col gap-1">
              {NAV.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      [
                        "flex items-center gap-3 px-3 py-3 rounded-xl text-sm tracking-wide transition",
                        isActive
                          ? "bg-white/[0.06] text-white"
                          : "text-white/70 hover:bg-white/[0.03]",
                      ].join(" ")
                    }
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </NavLink>
                );
              })}
              <NavLink
                to="/auth"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-auth"
                className="flex items-center gap-3 px-3 py-3 mt-2 rounded-xl text-sm tracking-wide text-amber-300 border border-amber-500/25 hover:bg-amber-500/10"
              >
                <LogIn className="w-4 h-4" />
                Accedi / Registrati
              </NavLink>
              {isAuthenticated && (
                <>
                  <NavLink
                    to="/area-personale"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-area"
                    className="flex items-center gap-3 px-3 py-3 mt-2 rounded-xl text-sm tracking-wide text-emerald-300 border border-emerald-500/25 hover:bg-emerald-500/10"
                  >
                    <UserCircle2 className="w-4 h-4" />
                    Area personale
                  </NavLink>
                  <button
                    onClick={handleLogout}
                    data-testid="mobile-nav-logout"
                    className="flex items-center gap-3 px-3 py-3 mt-2 rounded-xl text-sm tracking-wide text-white/70 border border-white/10 hover:bg-white/5"
                  >
                    <LogOut className="w-4 h-4" />
                    Esci
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* NDA gate — renders only when an authenticated user has not
          yet accepted the current NDA version. Anonymous visitors are
          never blocked; public marketing routes stay fully accessible. */}
      <NdaGate />

      {/* Footer — pb tuned to leave breathing room above the fixed Iubenda
          US Privacy widget (~94px on mobile, smaller on desktop). Uses
          env(safe-area-inset-bottom) so iOS home-bar / notched devices
          also render the legal links comfortably above the pill. */}
      <footer className="border-t border-white/5 pt-8 mt-16 pb-[calc(112px+env(safe-area-inset-bottom))] sm:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] tracking-[0.28em] uppercase text-white/30">
            <span>TIME Inside · Ecosistema TIME</span>
            <span>Il Tempo è la nostra vera moneta</span>
          </div>
          <nav
            data-testid="footer-legal-nav"
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-white/40"
          >
            <a
              data-testid="footer-privacy"
              href="https://www.iubenda.com/privacy-policy/61078636"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-white transition-colors"
              title="Privacy Policy"
            >
              Privacy Policy
            </a>
            <span aria-hidden="true" className="text-white/15">·</span>
            <a
              data-testid="footer-cookie"
              href="https://www.iubenda.com/privacy-policy/61078636/cookie-policy"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-white transition-colors"
              title="Cookie Policy"
            >
              Cookie Policy
            </a>
            <span aria-hidden="true" className="text-white/15">·</span>
            <a
              data-testid="footer-termini"
              href="https://www.iubenda.com/termini-e-condizioni/61078636"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-white transition-colors"
              title="Termini e Condizioni"
            >
              Termini e Condizioni
            </a>
            <span aria-hidden="true" className="text-white/15">·</span>
            <button
              type="button"
              onClick={() => {
                try {
                  if (
                    window._iub &&
                    window._iub.cs &&
                    window._iub.cs.api &&
                    typeof window._iub.cs.api.openPreferences === "function"
                  ) {
                    window._iub.cs.api.openPreferences();
                  }
                } catch (e) {
                  /* ignore — Iubenda widget will load and expose _iub */
                }
              }}
              data-testid="footer-manage-cookies"
              className="iubenda-cs-preferences-link hover:text-white transition-colors"
            >
              Gestisci cookie
            </button>
          </nav>
        </div>
      </footer>
    </div>
  );
}
